import React from 'react';
import { SealCheck, ArrowRight } from '@phosphor-icons/react';
import { SectionHeading, money } from './ProjectOverviewSections';

const pct = (v) => (v === null || v === undefined ? null : `${Number(v).toFixed(1)}%`);
const months = (v) => (v === null || v === undefined ? null : `${v} months`);

export const ExitPerformanceHeader = ({ performance, onViewFinancials }) => {
  if (!performance) return null;
  const actualReturn = pct(performance.actual_return_pct);
  const secondary = [
    [performance.actual_hold_months ? `${performance.actual_hold_months} mo` : null, 'Actual hold'],
    [money(performance.capital_returned ?? performance.investor_distribution), 'Returned to investors'],
  ].filter(([v]) => v);

  if (!actualReturn && !secondary.length) return null;

  return (
    <section
      className="rounded-2xl bg-white border border-black/[0.05] shadow-[0_16px_40px_-28px_rgba(0,0,0,0.35)] overflow-hidden"
      data-testid="exit-performance-header"
    >
      <div className="p-5 md:p-6">
        <div className="flex items-center gap-2.5">
          <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[rgb(var(--foreground))] text-white">
            Exited
          </span>
          {performance.exit_date_label && (
            <span className="text-xs text-foreground-muted">{performance.exit_date_label}</span>
          )}
        </div>

        {actualReturn && (
          <div className="mt-5 text-center">
            <div className="flex items-start justify-center gap-2">
              <p
                className="text-5xl md:text-6xl font-heading font-semibold tracking-[-0.03em] leading-[0.9]"
                data-testid="exit-actual-return"
              >
                {actualReturn}
              </p>
              <SealCheck
                size={22}
                weight="fill"
                className="text-[rgb(34,150,94)] mt-1 flex-shrink-0"
                data-testid="exit-verified-icon"
              />
            </div>
            <p className="text-xs text-foreground-muted mt-2.5">
              Actual return
              {performance.variance_pp != null && performance.projected_return_pct != null && (
                <>
                  {' · '}vs {pct(performance.projected_return_pct)} projected{' '}
                  <span
                    className={
                      performance.variance_pp >= 0
                        ? 'text-[rgb(34,150,94)] font-medium'
                        : 'text-[rgb(200,60,60)] font-medium'
                    }
                  >
                    ({performance.variance_pp >= 0 ? '+' : ''}
                    {performance.variance_pp} pp)
                  </span>
                </>
              )}
            </p>
          </div>
        )}

        {secondary.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-6 pt-5 border-t border-black/[0.06]">
            {secondary.map(([value, label]) => (
              <div key={label} className="min-w-0">
                <p className="text-lg font-heading font-semibold tracking-tight leading-none truncate">{value}</p>
                <p className="text-[11px] text-foreground-muted mt-1.5 leading-tight">{label}</p>
              </div>
            ))}
          </div>
        )}

        {performance.methodology_note && (
          <p className="text-xs text-foreground-muted/80 mt-4">{performance.methodology_note}</p>
        )}
      </div>

      {onViewFinancials && (
        <button
          onClick={onViewFinancials}
          className="w-full flex items-center justify-center gap-2 px-5 py-3.5 text-sm font-medium border-t border-black/[0.06] hover:bg-black/[0.03] transition-colors"
          data-testid="exit-view-financials-button"
        >
          See the financials
          <ArrowRight size={16} weight="bold" />
        </button>
      )}
    </section>
  );
};

const unitFormat = (value, unit) => {
  if (value === null || value === undefined || value === '') return null;
  if (unit === 'percent') return pct(value);
  if (unit === 'months') return months(value);
  return money(value);
};

const varianceLabel = (row) => {
  const { plan, actual, unit, higher_is_better: higherIsBetter = true } = row;
  if (plan === null || plan === undefined || actual === null || actual === undefined) return null;
  const diff = Number(actual) - Number(plan);
  if (!Number.isFinite(diff) || Math.round(diff * 100) === 0) return { text: 'On plan', tone: 'flat' };
  const sign = diff > 0 ? '+' : '-';
  const magnitude =
    unit === 'percent'
      ? `${Math.abs(diff).toFixed(1)} pp`
      : unit === 'months'
        ? `${Math.abs(diff)} mo`
        : money(Math.abs(diff));
  const good = higherIsBetter ? diff > 0 : diff < 0;
  return { text: `${sign}${magnitude}`, tone: good ? 'good' : 'bad' };
};

const toneClass = {
  good: 'text-[rgb(34,150,94)]',
  bad: 'text-[rgb(200,60,60)]',
  flat: 'text-foreground-muted',
};

export const ProjectedVsActual = ({ performance }) => {
  if (!performance) return null;

  // The backend builds this from the live ledger, so it follows whatever finance edits in admin
  const rows = (performance.plan_vs_actual || []).filter(
    (row) => row.plan !== null || row.actual !== null,
  );
  if (!rows.length) return null;

  return (
    <section data-testid="projected-vs-actual">
      <SectionHeading>Project performance</SectionHeading>
      <div className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-x-4 md:gap-x-6 pb-3 border-b border-black/[0.08]">
        <span />
        <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted text-right">Plan</p>
        <p className="text-[11px] uppercase tracking-[0.14em] text-primary font-medium text-right">Actual</p>
        <p className="text-[11px] uppercase tracking-[0.14em] text-foreground-muted text-right">Variance</p>
      </div>
      <div className="divide-y divide-black/[0.06]">
        {rows.map((row) => {
          const variance = varianceLabel(row);
          return (
            <div
              key={row.label}
              className="grid grid-cols-[1.4fr_1fr_1fr_1fr] gap-x-4 md:gap-x-6 py-3.5 items-baseline"
              data-testid={`performance-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <p className="text-sm text-foreground-muted">{row.label}</p>
              <p className="text-sm text-right tabular-nums">{unitFormat(row.plan, row.unit) || '—'}</p>
              <p className="text-sm font-medium text-right tabular-nums">{unitFormat(row.actual, row.unit) || '—'}</p>
              <p className={`text-sm text-right tabular-nums ${variance ? toneClass[variance.tone] : 'text-foreground-muted'}`}>
                {variance ? variance.text : '—'}
              </p>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-foreground-muted mt-4">
        Plan figures come from the approved budget and the projection frozen at approval. Actuals are the posted
        figures and update as the project reports.
      </p>
    </section>
  );
};
